// filepath: d:\projects\giveinkind\inkind-monorepo\packages\@gik\core\pages\InkindsSitemap.tsx
import { PageSection } from '@gik/ui/gik/PageSection';
import React from 'react';
import bemBlock from '../utils/bemBlock';
import { SitemapSectionView } from './SitemapSectionView';

import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { ErrorPageSection } from './Error/ErrorPageSection';
import { useEnvStore } from '../store/EnvStore';
import { useSiteMap } from '@gik/api/legal';

// Number of items to display per page
const ITEMS_PER_PAGE = 1000;

type InkindsSitemapCompProps = {
  page?: number;
  perPage?: number;
  siteMapData?: { url: string; title: string }[];
};

function InkindsSitemapComp({ page = 0, perPage = ITEMS_PER_PAGE, siteMapData }: InkindsSitemapCompProps) {
  const bem = bemBlock('sitemap');
  // Use the SSR data if available, otherwise fall back to SWR (client-side navigation)
  const data = siteMapData ?? useSiteMap().data;
  const frontendBaseUrl = useEnvStore.getState().FRONTEND_BASE_URL || '/';

  const prefixes = {
    pages: 'inkinds',
  };

  const allPages = React.useMemo(() => {
    return (
      data
        ?.filter(item => item.url.startsWith(prefixes.pages + '/'))
        .map(page => {
          return {
            url: page.url.replace(prefixes.pages + '/', ''),
            title: page.title,
          };
        }) || []
    );
  }, [data, prefixes.pages]);

  // Calculate total number of pages based on items per page
  const totalPages = Math.ceil((allPages?.length || 0) / perPage);

  // Get the current page's items
  const currentPageItems = React.useMemo(() => {
    if (page === 0) {
      // If page is 0, we're on the main index page
      return [];
    }

    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return allPages.slice(startIndex, endIndex);
  }, [allPages, page, perPage]);

  if (page === 0) {
    // Display links to all paginated pages
    return (
      <PageSection className={bem()}>
        <h1>Inkind Pages Sitemap</h1>
        <div>
          <a href="/sitemap">← Back to Index</a>
        </div>
        <p>{allPages.length} total Inkind pages found. Browse sitemap pages below:</p>
        <div>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
            <div key={pageNum}>
              <a href={`/inkinds/sitemap?page=${pageNum}&per_page=${perPage}`}>
                Page {pageNum} ({(pageNum - 1) * perPage + 1}-{Math.min(pageNum * perPage, allPages.length)})
              </a>
            </div>
          ))}
        </div>
      </PageSection>
    );
  }

  return (
    <PageSection className={bem()}>
      <h1>
        Inkind Pages Sitemap - Page {page} of {totalPages}
      </h1>
      <div>
        <div>
          <a href="/inkinds/sitemap">← Back to Index</a>
        </div>
        <div className="tw-flex tw-gap-8">
          {page > 1 && (
            <div>
              <a href={`/inkinds/sitemap?page=${page - 1}&per_page=${perPage}`}>← Previous Page</a>
            </div>
          )}
          {page < totalPages && (
            <div>
              <a href={`/inkinds/sitemap?page=${page + 1}&per_page=${perPage}`}>Next Page →</a>
            </div>
          )}
        </div>
      </div>
      <SitemapSectionView
        baseUrl={frontendBaseUrl}
        title={`Inkind Pages (${(page - 1) * perPage + 1}-${Math.min(page * perPage, allPages.length)})`}
        items={currentPageItems}
        prefix={prefixes.pages}
      />
    </PageSection>
  );
}

export const InkindsSitemapPage = withComponentErrorBoundary(InkindsSitemapComp, { ErrorFallback: ErrorPageSection });
